import {
  Button,
  cx,
  Dropdown,
  LoadingIndicator,
  Tabs,
} from "@light-technology/components";
import { AlignVerticalCenter01, Expand05, Settings04 } from "@untitledui/icons";
import { useState } from "react";
import type { Control } from "react-hook-form";
import {
  type CompareAppPlanResponse,
  type PlanDetailResponse,
} from "../../state/api.types";
import { usePlanComparison } from "../PlanComparisonTable/common";
import {
  useUpdatedPlanComparison,
  type FormValues,
  type ServiceTerritory,
  type TermMonths,
  type UsageKwh,
} from "./common";
import { ComparisonTable } from "./ComparisonTable";
import { PlanComparisonTable } from "./PlanComparisonTable";
import { ServiceTerritorySelect } from "./ServiceTerritorySelect";
import { TermMonthsSelect } from "./TermMonthsSelect";
import { UsageKwhSelect } from "./UsageKwhSelect";

type ActiveTab = "summary" | "competition";

const TABS = [
  { id: "summary", label: "Summary" },
  { id: "competition", label: "Competition" },
] as const satisfies { id: ActiveTab; label: string }[];

type PlanEditComparisonTableProps = {
  plan: PlanDetailResponse;
  control: Control<FormValues>;
  serviceTerritory: ServiceTerritory;
  setServiceTerritory: (value: ServiceTerritory) => void;
  termMonths: TermMonths;
  setTermMonths: (value: TermMonths) => void;
  planComparison: ReturnType<typeof usePlanComparison>;
  isExpanded: boolean;
  setIsExpanded: (value: (prev: boolean) => boolean) => void;
};

export const PlanEditComparisonTable = ({
  plan,
  control,
  serviceTerritory,
  setServiceTerritory,
  termMonths,
  setTermMonths,
  planComparison,
  isExpanded,
  setIsExpanded,
}: PlanEditComparisonTableProps) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>("summary");
  const [usageKwh, setUsageKwh] = useState<UsageKwh>(1000);
  const [economicsOnly, setEconomicsOnly] = useState(false);

  const updatedPlanComparison = useUpdatedPlanComparison(
    plan,
    serviceTerritory,
    termMonths,
    control,
  );

  // Track the last known good pair atomically — both columns must come from the
  // same request cycle to avoid mismatched highlighting while one is still loading.
  const [lastPair, setLastPair] = useState<
    | {
        live: CompareAppPlanResponse;
        updated: CompareAppPlanResponse;
      }
    | undefined
  >(() =>
    planComparison.data && updatedPlanComparison.data
      ? { live: planComparison.data, updated: updatedPlanComparison.data }
      : undefined,
  );

  if (
    planComparison.data &&
    updatedPlanComparison.data &&
    (planComparison.data !== lastPair?.live ||
      updatedPlanComparison.data !== lastPair.updated)
  ) {
    setLastPair({
      live: planComparison.data,
      updated: updatedPlanComparison.data,
    });
  }

  // Use current data only when both are available; otherwise fall back to the last
  // good pair so neither column shows a stale/mismatched state mid-reload.
  const bothCurrent = !!(planComparison.data && updatedPlanComparison.data);
  const livePlanData = bothCurrent ? planComparison.data : lastPair?.live;
  const updatedPlanData = bothCurrent
    ? updatedPlanComparison.data
    : lastPair?.updated;

  // Only show spinner on initial load — if stale data exists, let it render through reloads.
  // If a request finishes (success or error) with no data, fall through to the error state.
  const isLoading =
    (!livePlanData &&
      (planComparison.isLoading || planComparison.isUninitialized)) ||
    (!updatedPlanData &&
      (updatedPlanComparison.isLoading ||
        updatedPlanComparison.isUninitialized));

  return (
    <div
      className={cx("py-4xl @page-sm:py-0 @page-md:min-w-0 @page-md:flex-3")}
    >
      <div className="sticky top-8">
        <div className="flex flex-col gap-3xl">
          {/*
           * Tabs.List must be a direct child of Tabs — React Aria's collection
           * pre-render traverses the whole subtree, so any wrapper div between
           * Tabs and Tabs.List breaks the ListBox selection context.
           * The settings button lives as a sibling of Tabs in the outer flex row.
           */}
          <div className="flex w-fit items-center gap-xl self-center">
            <Tabs
              selectedKey={activeTab}
              onSelectionChange={(key) => {
                setActiveTab(key as ActiveTab);
              }}
            >
              <Tabs.List
                className="w-min p-0"
                type="button-border"
                size="sm"
                items={TABS}
              >
                {(tab) => (
                  <Tabs.Item
                    className="rounded-[10px] border-1 border-transparent shadow-none ring-inset data-hovered:bg-transparent data-selected:border-primary"
                    {...tab}
                  />
                )}
              </Tabs.List>
            </Tabs>
            <div className="absolute right-0 flex gap-md">
              <Dropdown.Root>
                <Button
                  iconLeading={Settings04}
                  aria-label="View settings"
                  size="sm"
                  color="secondary"
                  tooltip="Offer settings"
                  isDisabled={isLoading || !livePlanData || !updatedPlanData}
                />
                <Dropdown.Popover placement="bottom end">
                  <div className="flex flex-col gap-xl p-2xl">
                    <div className="flex flex-col">
                      <p className="text-md font-semibold">Offer settings</p>
                      <p className="text-sm text-tertiary">
                        Preview rates for this plan based on different customer
                        scenarios.
                      </p>
                    </div>
                    <ServiceTerritorySelect
                      value={serviceTerritory}
                      onChange={setServiceTerritory}
                    />
                    <TermMonthsSelect
                      value={termMonths}
                      onChange={setTermMonths}
                    />
                    <UsageKwhSelect value={usageKwh} onChange={setUsageKwh} />
                  </div>
                </Dropdown.Popover>
              </Dropdown.Root>
              <Button
                iconLeading={AlignVerticalCenter01}
                aria-label={
                  economicsOnly
                    ? "Show all rows"
                    : "Collapse to only show cost rows"
                }
                size="sm"
                color={economicsOnly ? "primary" : "secondary"}
                tooltip={
                  economicsOnly
                    ? "Show all rows"
                    : "Collapse to only show cost rows"
                }
                isDisabled={isLoading || !livePlanData || !updatedPlanData}
                onClick={() => {
                  setEconomicsOnly((v) => !v);
                }}
              />
              <Button
                className="hidden @page-md:block"
                iconLeading={Expand05}
                aria-label={isExpanded ? "Collapse table" : "Expand table"}
                size="sm"
                color={isExpanded ? "primary" : "secondary"}
                tooltip={
                  isExpanded
                    ? "Exit presentation mode"
                    : "View presentation mode"
                }
                onClick={() => {
                  setIsExpanded((v) => !v);
                }}
              />
            </div>
          </div>
          {/*
           * Both panels stay mounted so competition data loads on page load.
           * The hidden attribute hides the inactive panel without unmounting it.
           */}
          <div hidden={activeTab !== "summary"}>
            {isLoading ? (
              <LoadingIndicator type="brand-spinner" />
            ) : livePlanData && updatedPlanData ? (
              <ComparisonTable
                hasSolarBuyback={
                  plan.has_solar_buyback &&
                  plan.plan_type?.plan_type !== "RTW_SOLAR_BUYBACK"
                }
                livePlanComparison={livePlanData}
                updatedPlanComparison={updatedPlanData}
                usageKwh={usageKwh}
                economicsOnly={economicsOnly}
              />
            ) : (
              <div className="flex flex-col items-center gap-sm py-3xl text-center">
                <div className="text-sm font-medium text-primary">
                  Unable to load plan pricing
                </div>
                <div className="text-sm text-tertiary">
                  Please contact support if this problem persists.
                </div>
              </div>
            )}
          </div>
          <div hidden={activeTab !== "competition"}>
            <PlanComparisonTable
              plan={plan}
              serviceTerritory={serviceTerritory}
              planComparison={updatedPlanComparison}
              usageKwh={usageKwh}
              economicsOnly={economicsOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
